import { WebLayout } from "components/Layout";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import {
  Shield,
  Tool,
  CheckCircle,
  Layers,
  Grid as GridIcon,
  Package,
  Home,
  Aperture,
  Phone,
  MessageCircle,
} from "react-feather";
import patternNoiseImg from "assets/pattern-noise.jpeg";
import bannerImg from "assets/gallery/image_14.jpeg";
import doorsImg from "assets/doors.jpeg";
import windowsImg from "assets/windows.jpeg";
import stairsImg from "assets/stairs.jpeg";
import burglarProofImg from "assets/burglarproof.jpeg";

export function Landing() {
  // Parallax hero
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const yHero = useTransform(scrollYProgress, [0, 1], ["0%", "-20%"]);
  const opacityHero = useTransform(scrollYProgress, [0, 0.6], [1, 0.2]);

  // Generic variants
  const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    visible: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: { delay: 0.08 * i, duration: 0.5, ease: "easeOut" },
    }),
  };

  const services = [
    {
      title: "Windows",
      desc: "Secure, ventilated, and elegant window units.",
      icon: Home,
    },
    {
      title: "Doors",
      desc: "Stylish, reinforced entrance & service doors.",
      icon: Shield,
    },
    {
      title: "Burglarproofing",
      desc: "Safety with elegant, custom grills.",
      icon: CheckCircle,
    },
    {
      title: "Stairs & Railings",
      desc: "Durable staircases & decorative rails.",
      icon: Layers,
    },
    {
      title: "Furniture",
      desc: "Custom metal tables and chairs.",
      icon: Package,
    },
    {
      title: "Pagolas & Balconies",
      desc: "Outdoor shade & comfort structures.",
      icon: GridIcon,
    },
    {
      title: "Door & Window Fittings",
      desc: "Precision hardware installations.",
      icon: Tool,
    },
    {
      title: "Tangstands",
      desc: "Robust, functional stands and supports.",
      icon: Aperture,
    },
  ];

  const stats = [
    { label: "Years of Craft", value: "10+" },
    { label: "Projects Delivered", value: "500+" },
    { label: "Avg. Turnaround", value: "7–21 days" },
    { label: "Customer Satisfaction", value: "98%" },
  ];

  const features = [
    {
      title: "Quality Craftsmanship",
      body: "Tight welds, smooth finishes, and exacting tolerances.",
    },
    {
      title: "Custom Designs",
      body: "Tailored to your space, style, and budget.",
    },
    {
      title: "Durable Materials",
      body: "Built to endure Kampala weather and busy use.",
    },
    {
      title: "Local Expertise",
      body: "Based on Kasangati Road; serving Kampala & beyond.",
    },
  ];

  const projects = [
    {
      tag: "Doors",
      img: doorsImg,
      alt: "Steel security door",
      to: "/gallery",
    },
    {
      tag: "Windows",
      img: windowsImg,
      alt: "Decorative window grills",
      to: "/gallery",
    },
    { tag: "Stairs", img: stairsImg, alt: "Stair railings", to: "/gallery" },
    {
      tag: "Burglarproofing",
      img: burglarProofImg,
      alt: "Burglarproofing grills",
      to: "/gallery",
    },
  ];

  return (
    <WebLayout>
      {/* HERO */}
      <section
        ref={heroRef}
        className="relative overflow-hidden bg-gradient-to-b from-gray-950 via-gray-900 to-gray-900"
      >
        {/* Background Image */}
        <motion.img
          style={{ y: yHero, opacity: opacityHero }}
          src={bannerImg}
          alt="Showcase of metal doors, railings, and window grills"
          className="absolute inset-0 h-full w-full object-cover"
        />

        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/60 z-0" />

        {/* Content */}
        <div className="relative z-10 mx-auto max-w-7xl px-6 pt-28 pb-20 md:pt-36 md:pb-28">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="max-w-3xl"
          >
            <span className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-gray-300 backdrop-blur">
              Kampala • Custom Metal Fabrication
            </span>
            <h1 className="mt-4 text-4xl font-extrabold tracking-tight text-white md:text-6xl">
              Crafting <span className="text-green-400">Strength</span>,{" "}
              <span className="text-yellow-400">Style</span> &amp; Security in
              Every Design
            </h1>
            <p className="mt-4 text-gray-300 md:text-lg">
              Windows, doors, burglarproofing, stairs & railings, furniture,
              pagolas & balconies, and precision fittings—built to last and
              tailored to your space.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center rounded-lg bg-green-500 px-5 py-3 font-semibold text-black shadow hover:brightness-105"
              >
                Get a Free Quote <Phone size={18} className="ml-2" />
              </Link>
              <a
                href="https://wa.me/256779706977"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center rounded-lg border border-white/15 bg-white/5 px-5 py-3 font-semibold text-white hover:bg-white/10"
              >
                WhatsApp Chat <MessageCircle size={18} className="ml-2" />
              </a>
              <Link
                to="/gallery"
                className="inline-flex items-center rounded-lg border border-white/15 px-5 py-3 font-semibold text-white hover:bg-white/10"
              >
                View Projects
              </Link>
            </div>
          </motion.div>

          {/* STAT BAR */}
          <div className="mt-10 grid grid-cols-2 gap-4 rounded-xl border border-white/10 bg-white/5 p-4 backdrop-blur md:grid-cols-4">
            {stats.map((s, i) => (
              <motion.div
                key={s.label}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
                variants={fadeUp}
                custom={i}
                className="rounded-lg p-3 text-center"
              >
                <div className="text-2xl font-extrabold text-white">
                  {s.value}
                </div>
                <div className="text-xs text-gray-400">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-gray-950 py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-6">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-2xl font-bold text-white md:text-3xl"
          >
            Our Services
          </motion.h2>
          <p className="mt-2 max-w-2xl text-gray-400">
            Custom metal fabrication with quality craftsmanship and durable
            materials.
          </p>

          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {services.map((srv, i) => {
              const Icon = srv.icon;
              return (
                <motion.div
                  key={srv.title}
                  initial={{ opacity: 0, y: 14, scale: 0.98 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{
                    delay: 0.05 * i,
                    duration: 0.45,
                    ease: "easeOut",
                  }}
                  className="group rounded-2xl border border-white/10 bg-gradient-to-b from-gray-900 to-gray-950 p-5 hover:border-green-500/40"
                >
                  <div className="flex items-center justify-between">
                    <div className="rounded-lg bg-white/5 p-2">
                      <Icon size={22} className="text-green-400" />
                    </div>
                    <span className="text-xs text-white/50">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-white">
                    {srv.title}
                  </h3>
                  <p className="mt-1 text-sm text-gray-400">{srv.desc}</p>
                  <Link
                    to="/services"
                    className="mt-4 inline-block text-sm font-semibold text-green-400 underline-offset-4 hover:underline"
                  >
                    Learn more
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FEATURED PROJECTS */}
      <section className="bg-gray-900 py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div>
              <h2 className="text-2xl font-bold text-white md:text-3xl">
                Featured Projects
              </h2>
              <p className="mt-2 max-w-xl text-gray-400">
                A small selection of our recent work. Tap any image to explore
                the full gallery.
              </p>
            </div>
            <Link
              to="/gallery"
              className="inline-flex items-center rounded-lg border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10"
            >
              Browse Gallery
            </Link>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {projects.map((p, i) => (
              <motion.div
                key={p.img}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ delay: 0.05 * i, duration: 0.45 }}
                className="group relative cursor-pointer overflow-hidden rounded-2xl border border-white/10"
              >
                <Link to={p.to} aria-label={`Open ${p.tag} in gallery`}>
                  <img
                    src={p.img}
                    alt={p.alt}
                    className="h-60 w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/50 via-black/0 to-transparent" />
                  <div className="absolute left-3 bottom-3 rounded-md bg-black/50 px-2 py-1 text-xs font-semibold text-white backdrop-blur">
                    {p.tag}
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="bg-gray-950 py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid items-center gap-10 md:grid-cols-2">
            <div>
              <h2 className="text-2xl font-bold text-white md:text-3xl">
                Why Choose Us
              </h2>
              <p className="mt-2 text-gray-400">
                We blend precision fabrication with design that elevates your
                space.
              </p>
              <ul className="mt-6 space-y-4">
                {features.map((f, i) => (
                  <motion.li
                    key={f.title}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={fadeUp}
                    custom={i}
                    className="flex items-start gap-3"
                  >
                    <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-md bg-green-500/15">
                      <CheckCircle size={16} className="text-green-400" />
                    </span>
                    <div>
                      <div className="font-semibold text-white">{f.title}</div>
                      <div className="text-sm text-gray-400">{f.body}</div>
                    </div>
                  </motion.li>
                ))}
              </ul>

              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/contact"
                  className="inline-flex items-center rounded-lg bg-green-500 px-5 py-3 font-semibold text-black shadow hover:brightness-105"
                >
                  Request a Quote
                </Link>
                <Link
                  to="/services"
                  className="inline-flex items-center rounded-lg border border-white/15 px-5 py-3 font-semibold text-white hover:bg-white/10"
                >
                  See All Services
                </Link>
              </div>
            </div>

            {/* Side visual / trust panel */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.5 }}
              className="rounded-2xl border border-white/10 bg-gradient-to-b from-gray-900 to-gray-950 p-6"
            >
              <div className="grid grid-cols-2 gap-4">
                {[
                  { k: "Material", v: "Steel, Wrought Iron, Aluminum" },
                  { k: "Finish", v: "Powder Coat, Galvanized, Paint" },
                  { k: "Site Work", v: "Measure, Fabricate, Install" },
                  { k: "Support", v: "Warranty & Maintenance" },
                ].map((row) => (
                  <div
                    key={row.k}
                    className="rounded-lg border border-white/10 p-4"
                  >
                    <div className="text-xs uppercase tracking-wide text-white/60">
                      {row.k}
                    </div>
                    <div className="mt-1 text-sm font-semibold text-white">
                      {row.v}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 rounded-xl bg-white/5 p-4 text-sm text-gray-300">
                Need a site visit? We’ll come measure and recommend the best
                design for your space.
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-gray-900 py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-6">
          <h2 className="text-2xl font-bold text-white md:text-3xl">
            Our Process
          </h2>
          <p className="mt-2 max-w-2xl text-gray-400">
            Clear steps from idea to installation—so you always know what’s
            next.
          </p>
          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-4">
            {[
              {
                n: "01",
                t: "Consult",
                d: "We discuss your needs, budget, and timeline.",
              },
              {
                n: "02",
                t: "Design",
                d: "Measurements & drawings for your approval.",
              },
              {
                n: "03",
                t: "Fabricate",
                d: "Precision welding, fit-up, and finishing.",
              },
              {
                n: "04",
                t: "Install",
                d: "Clean, safe installation and handover.",
              },
            ].map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.06 * i, duration: 0.4 }}
                className="rounded-2xl border border-white/10 bg-gradient-to-b from-gray-900 to-gray-950 p-5"
              >
                <div className="text-sm text-white/60">{s.n}</div>
                <div className="mt-1 text-lg font-semibold text-white">
                  {s.t}
                </div>
                <div className="mt-1 text-sm text-gray-400">{s.d}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS (lightweight) */}
      <section className="bg-gray-950 py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-6">
          <h2 className="text-2xl font-bold text-white md:text-3xl">
            What Clients Say
          </h2>
          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
            {[
              {
                q: "Our balcony rails are stunning and solid. Installation was neat and fast.",
                a: "— Sarah N., Naguru",
              },
              {
                q: "They customized burglarproofing that actually matches our décor.",
                a: "— Daniel K., Ntinda",
              },
              {
                q: "Great communication, fair pricing, and top-tier finishing.",
                a: "— Amina B., Kira",
              },
            ].map((t, i) => (
              <motion.blockquote
                key={i}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.05 * i, duration: 0.4 }}
                className="rounded-2xl border border-white/10 bg-gradient-to-b from-gray-900 to-gray-950 p-6 text-gray-300"
              >
                “{t.q}”
                <footer className="mt-3 text-sm text-white/70">{t.a}</footer>
              </motion.blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="relative overflow-hidden bg-gradient-to-r from-green-500 to-yellow-500">
        <div
          className="absolute inset-0 opacity-10 mix-blend-multiply"
          style={{ backgroundImage: `url(${patternNoiseImg})` }}
        />
        <div className="relative mx-auto max-w-7xl px-6 py-12 md:py-16">
          <div className="grid items-center gap-6 md:grid-cols-3">
            <div className="md:col-span-2">
              <h3 className="text-2xl font-extrabold text-black md:text-3xl">
                Ready to upgrade your space with premium metal work?
              </h3>
              <p className="mt-1 text-black/80">
                Book a site visit or send your measurements—we’ll prepare a free
                quote.
              </p>
            </div>
            <div className="flex gap-3 md:justify-end">
              <Link
                to="/contact"
                className="inline-flex items-center rounded-lg bg-black px-5 py-3 font-semibold text-white shadow hover:opacity-90"
              >
                Contact Us
              </Link>
              <a
                href="https://wa.me/256779706977"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center rounded-lg border border-black/20 bg-white/90 px-5 py-3 font-semibold text-black hover:bg-white"
              >
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>
    </WebLayout>
  );
}
